import { ApiResponse } from 'apisauce';
import rootReducer from '../store/Reducers';
import Store from '../store/Store';
import Api from './Api';

const store: any = Store({}, rootReducer).getState();
const baseUrl = store.Application.backendURL;

function sanitizeDownloadFileName(fileName: string): string {
  const sanitized = fileName
    .replace(/[\\/:*?"<>|\x00-\x1F\x7F]/g, '-')
    .replace(/^\.+/, '')
    .trim();

  return sanitized || 'crash-export.json.gz';
}

export interface ICrashReportSummary {
  reportId: number;
  reportType: string;
  date: string;
  status: string;
  queue: string;
}

export interface ICrashImportResult {
  originalCrashKey: string;
  entitiesImported: number;
  entitiesReplaced: number;
}

/**
 * Searches for reports sharing the given original crash key, returning a summary
 * (date, status and current workflow queue) for each matching report.
 */
export async function searchByOriginalCrashKey(
  originalCrashKey: string
): Promise<ApiResponse<ICrashReportSummary[]>> {
  return Api.get(
    `${baseUrl}Export/Search/${encodeURIComponent(originalCrashKey)}`
  );
}

/**
 * Downloads the gzip-compressed export package for the given original crash key
 * and triggers a browser download.
 */
export async function downloadExport(originalCrashKey: string): Promise<void> {
  const response: ApiResponse<Blob> = await Api.get(
    `${baseUrl}Export/${encodeURIComponent(originalCrashKey)}`,
    {},
    { responseType: 'blob' }
  );

  if (!response.ok || !response.data) {
    throw new Error('Failed to download export.');
  }

  const disposition =
    (response.headers &&
      (response.headers['content-disposition'] as string)) ||
    '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match
    ? sanitizeDownloadFileName(decodeURIComponent(match[1]))
    : sanitizeDownloadFileName(`crash-export-${originalCrashKey}.json.gz`);

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * Imports a previously exported crash package. When overwrite is true, an existing
 * crash matching the package's original crash key is replaced.
 */
export async function importExport(
  file: File,
  overwrite: boolean
): Promise<ApiResponse<ICrashImportResult>> {
  const formData = new FormData();
  formData.append('file', file);

  return Api.post(`${baseUrl}Export/Import?overwrite=${overwrite}`, formData);
}
